import {t, useLanguage} from '../i18n';
// src/sections/GallerySection.jsx
// LOOP BUSINESSPAGE GALLERY PUBLISHING V4
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IMAGE_UPLOAD_ACCEPT, prepareImageFilesForUpload } from "../utils/imageUploadUtils";

function resolveProjectUrl(project) {
  if (!project) return "";
  if (typeof project === "string") return project.trim();
  return String(project.url || project.imageUrl || project.src || project.downloadURL || "").trim();
}

function resolveProjectName(project, index) {
  if (project && typeof project === "object") {
    const value = String(project.name || project.title || project.caption || "").trim();
    if (value) return value;
  }
  return `Foto ${index + 1}`;
}

function GallerySwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      className={`gallery-v4-switch ${checked ? "is-on" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      aria-pressed={checked}
      aria-label={t("Mostrar galería en el perfil público")}
    >
      <span />
    </button>
  );
}

export default function GallerySection({
  businessId = null,
  businessDoc = {},
  projects = [],
  uploading = false,
  uploadPct = 0,
  onUploadProjects = async () => {},
  onRemoveProject = async () => {},
  onVisibilityChange = async () => {},
}) {
  useLanguage();
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("");
  const [visible, setVisible] = useState(
    businessDoc?.showProjectsOnBusinessPage !== false &&
    businessDoc?.hideProjectsOnBusinessPage !== true
  );
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [removingUrl, setRemovingUrl] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    setVisible(
      businessDoc?.showProjectsOnBusinessPage !== false &&
      businessDoc?.hideProjectsOnBusinessPage !== true
    );
  }, [businessDoc?.showProjectsOnBusinessPage, businessDoc?.hideProjectsOnBusinessPage]);

  const galleryItems = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(projects) ? projects : [])
      .map((project, index) => {
        const url = resolveProjectUrl(project);
        if (!url || seen.has(url)) return null;
        seen.add(url);
        return {
          id: project && typeof project === "object" && project.id ? project.id : `gallery-${index}`,
          url,
          name: resolveProjectName(project, index),
        };
      })
      .filter(Boolean);
  }, [projects]);

  const chooseFiles = async (event) => {
    const selected = Array.from(event?.target?.files || []);
    try { if (event?.target) event.target.value = null; } catch (_) {}
    if (!selected.length) return;

    if (selected.length > 12) {
      setStatus("Selecciona hasta 12 fotos por subida.");
      return;
    }

    const prepared = await prepareImageFilesForUpload(selected, {
      purpose: "foto de galería",
      namePrefix: "loop-gallery",
      maxMb: 18,
      setStatus,
    });

    if (!prepared.ok) {
      setStatus(prepared.message || "No pudimos preparar esas imágenes.");
      return;
    }

    setFiles(prepared.files);
    setStatus(
      `${prepared.files.length} foto${prepared.files.length === 1 ? "" : "s"} lista${prepared.files.length === 1 ? "" : "s"} para subir.`
    );
  };

  const upload = async () => {
    if (!files.length || uploading) return;
    try {
      await onUploadProjects(files);
      setFiles([]);
      setStatus("Fotos publicadas en tu galería.");
      setTimeout(() => setStatus(""), 1800);
    } catch (_) {
      // Parent already owns the error surface.
    }
  };

  const changeVisibility = async (next) => {
    if (!businessId || savingVisibility) return;
    setSavingVisibility(true);
    try {
      await onVisibilityChange(next);
      setVisible(next);
    } finally {
      setSavingVisibility(false);
    }
  };

  const remove = async (item) => {
    if (!item?.url || removingUrl) return;
    const ok = window.confirm("¿Quitar esta foto de la galería pública?");
    if (!ok) return;

    setRemovingUrl(item.url);
    try {
      await onRemoveProject(item.url);
    } finally {
      setRemovingUrl("");
    }
  };

  return (
    <div className="gallery-v4-shell">
      <style>{styles}</style>

      <section className="gallery-v4-panel">
        <header className="gallery-v4-head">
          <div>
            <span className="gallery-v4-kicker">{t("Galería")}</span>
            <h2>{t("Fotos del negocio")}</h2>
            <p>
              Publica el espacio, resultados, trabajos y momentos que ayudan a tus clientes
              a conocer mejor tu negocio antes de reservar.
            </p>
          </div>

          <div className="gallery-v4-visibility">
            <div>
              <strong>{visible ? t("Visible") : t("Oculta")}</strong>
              <span>{galleryItems.length}{" " + t("foto")}{galleryItems.length === 1 ? "" : "s"}{" " + t("publicadas")}</span>
            </div>
            <GallerySwitch
              checked={visible}
              onChange={changeVisibility}
              disabled={!businessId || savingVisibility}
            />
          </div>
        </header>

        <div className="gallery-v4-body">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={IMAGE_UPLOAD_ACCEPT}
            onChange={chooseFiles}
            hidden
          />

          <div className="gallery-v4-upload">
            <div className="gallery-v4-upload-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 16V5" />
                <path d="m8 9 4-4 4 4" />
                <path d="M5 15.5v2A1.5 1.5 0 0 0 6.5 19h11a1.5 1.5 0 0 0 1.5-1.5v-2" />
              </svg>
            </div>

            <div className="gallery-v4-upload-copy">
              <strong>{t("Añade fotos a tu perfil")}</strong>
              <span>{t("JPG, PNG, WEBP o HEIC/HEIF. Hasta 12 imágenes por subida.")}</span>
              {status ? <small>{status}</small> : null}
            </div>

            <div className="gallery-v4-upload-actions">
              <button
                type="button"
                className="gallery-v4-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >{t("Seleccionar fotos")}</button>
              <button
                type="button"
                className="gallery-v4-btn is-primary"
                onClick={upload}
                disabled={!files.length || uploading}
              >
                {uploading ? "Subiendo…" : files.length ? `Publicar ${files.length}` : "Publicar"}
              </button>
            </div>
          </div>

          {uploading || uploadPct > 0 ? (
            <div className="gallery-v4-progress" aria-live="polite">
              <div>
                <span style={{ width: `${Math.max(4, Math.min(100, Number(uploadPct || 0)))}%` }} />
              </div>
              <small>{t("Subiendo fotos ·") + " "}{Math.round(Number(uploadPct || 0))}%</small>
            </div>
          ) : null}

          <div className="gallery-v4-list-head">
            <div>
              <strong>{t("Galería pública")}</strong>
              <span>{t("Estas fotos aparecerán en Business Page.")}</span>
            </div>
            <em>{galleryItems.length}/50</em>
          </div>

          {galleryItems.length === 0 ? (
            <div className="gallery-v4-empty">
              <div className="gallery-v4-empty-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="3.5" y="5" width="17" height="14" rx="3" />
                  <circle cx="9" cy="10" r="1.5" />
                  <path d="m6 17 4.6-4.6 3.1 3.1 2-2 2.8 3.5" />
                </svg>
              </div>
              <div>
                <strong>{t("Tu galería todavía está vacía")}</strong>
                <span>{t("Empieza con 4–8 fotos reales del espacio, trabajos o resultados.")}</span>
              </div>
            </div>
          ) : (
            <div className="gallery-v4-grid">
              {galleryItems.map((item, index) => (
                <article className="gallery-v4-photo" key={item.url}>
                  <img src={item.url} alt={item.name || `Foto ${index + 1}`} loading="lazy" />
                  <div className="gallery-v4-photo-top">
                    <span>{index === 0 ? "Portada de galería" : `Foto ${index + 1}`}</span>
                  </div>
                  <button
                    type="button"
                    className="gallery-v4-remove"
                    onClick={() => remove(item)}
                    disabled={removingUrl === item.url}
                  >
                    {removingUrl === item.url ? "Quitando…" : t("Quitar")}
                  </button>
                </article>
              ))}
            </div>
          )}

          <p className="gallery-v4-footnote">{t("“Quitar” elimina la foto del perfil público. El archivo original puede permanecer en Storage para evitar eliminaciones accidentales.")}</p>
        </div>
      </section>
    </div>
  );
}

const styles = `
  .gallery-v4-shell{
    display:grid;
    min-width:0;
  }

  .gallery-v4-panel{
    min-width:0;
    border:1px solid rgba(226,232,240,.94);
    border-radius:28px;
    background:
      radial-gradient(circle at 100% 0%,rgba(0,103,95,.055),transparent 30%),
      #fff;
    box-shadow:0 18px 52px rgba(15,23,42,.06);
    overflow:hidden;
  }

  .gallery-v4-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:20px;
    padding:20px;
    border-bottom:1px solid rgba(241,245,249,.98);
  }

  .gallery-v4-kicker{
    display:block;
    margin-bottom:7px;
    color:#00796f;
    font-size:10px;
    line-height:1;
    letter-spacing:.16em;
    text-transform:uppercase;
    font-weight:950;
  }

  .gallery-v4-head h2{
    margin:0;
    color:#0f172a;
    font-size:25px;
    line-height:1;
    letter-spacing:-.055em;
    font-weight:950;
  }

  .gallery-v4-head p{
    max-width:680px;
    margin:9px 0 0;
    color:#64748b;
    font-size:13px;
    line-height:1.45;
    font-weight:720;
  }

  .gallery-v4-visibility{
    flex:0 0 auto;
    min-width:180px;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:14px;
    padding:9px 10px 9px 13px;
    border:1px solid #e2e8f0;
    border-radius:18px;
    background:rgba(255,255,255,.92);
  }

  .gallery-v4-visibility strong{
    display:block;
    color:#0f172a;
    font-size:12px;
    line-height:1.1;
    font-weight:950;
  }

  .gallery-v4-visibility span{
    display:block;
    margin-top:3px;
    color:#64748b;
    font-size:10px;
    line-height:1.2;
    font-weight:800;
  }

  .gallery-v4-switch{
    width:50px;
    height:30px;
    flex:0 0 50px;
    border:0;
    border-radius:999px;
    padding:3px;
    background:#cbd5e1;
    cursor:pointer;
    transition:background .16s ease;
  }

  .gallery-v4-switch span{
    display:block;
    width:24px;
    height:24px;
    border-radius:999px;
    background:#fff;
    box-shadow:0 3px 10px rgba(15,23,42,.17);
    transform:translateX(0);
    transition:transform .16s ease;
  }

  .gallery-v4-switch.is-on{background:#00796f}
  .gallery-v4-switch.is-on span{transform:translateX(20px)}
  .gallery-v4-switch:disabled{opacity:.52;cursor:not-allowed}

  .gallery-v4-body{
    padding:20px;
  }

  .gallery-v4-upload{
    display:grid;
    grid-template-columns:50px minmax(0,1fr) auto;
    gap:14px;
    align-items:center;
    padding:16px;
    border:1px dashed rgba(0,121,111,.30);
    border-radius:22px;
    background:
      radial-gradient(circle at 0% 0%,rgba(0,121,111,.065),transparent 36%),
      #fbfefd;
  }

  .gallery-v4-upload-icon{
    width:50px;
    height:50px;
    display:grid;
    place-items:center;
    border-radius:17px;
    border:1px solid rgba(0,121,111,.11);
    background:#eaf8f4;
    color:#00796f;
  }

  .gallery-v4-upload-icon svg,
  .gallery-v4-empty-icon svg{
    width:24px;
    height:24px;
    stroke:currentColor;
    stroke-width:2;
    stroke-linecap:round;
    stroke-linejoin:round;
  }

  .gallery-v4-upload-copy{
    min-width:0;
  }

  .gallery-v4-upload-copy strong{
    display:block;
    color:#0f172a;
    font-size:14px;
    line-height:1.15;
    font-weight:950;
  }

  .gallery-v4-upload-copy span{
    display:block;
    margin-top:4px;
    color:#64748b;
    font-size:11.5px;
    line-height:1.35;
    font-weight:720;
  }

  .gallery-v4-upload-copy small{
    display:block;
    margin-top:7px;
    color:#00796f;
    font-size:11px;
    line-height:1.2;
    font-weight:900;
  }

  .gallery-v4-upload-actions{
    display:flex;
    align-items:center;
    gap:8px;
  }

  .gallery-v4-btn{
    min-height:42px;
    border:1px solid #dce3ea;
    border-radius:14px;
    padding:0 14px;
    background:#fff;
    color:#334155;
    font-size:11.5px;
    line-height:1;
    font-weight:950;
    cursor:pointer;
  }

  .gallery-v4-btn.is-primary{
    border-color:#00796f;
    background:#00796f;
    color:#fff;
    box-shadow:0 12px 24px rgba(0,121,111,.14);
  }

  .gallery-v4-btn:disabled{
    opacity:.48;
    cursor:not-allowed;
    box-shadow:none;
  }

  .gallery-v4-progress{
    margin-top:12px;
    padding:10px 12px;
    border:1px solid #e2e8f0;
    border-radius:16px;
    background:#f8fafc;
  }

  .gallery-v4-progress > div{
    height:8px;
    overflow:hidden;
    border-radius:999px;
    background:#e2e8f0;
  }

  .gallery-v4-progress > div > span{
    display:block;
    height:100%;
    border-radius:inherit;
    background:linear-gradient(90deg,#00796f,#0ea895);
    transition:width .18s ease;
  }

  .gallery-v4-progress small{
    display:block;
    margin-top:6px;
    color:#64748b;
    font-size:10.5px;
    font-weight:850;
  }

  .gallery-v4-list-head{
    display:flex;
    align-items:end;
    justify-content:space-between;
    gap:14px;
    margin-top:20px;
  }

  .gallery-v4-list-head strong{
    display:block;
    color:#0f172a;
    font-size:14px;
    line-height:1.1;
    font-weight:950;
  }

  .gallery-v4-list-head span{
    display:block;
    margin-top:3px;
    color:#64748b;
    font-size:11px;
    font-weight:730;
  }

  .gallery-v4-list-head em{
    color:#00796f;
    font-size:11px;
    font-style:normal;
    font-weight:950;
  }

  .gallery-v4-grid{
    margin-top:11px;
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    gap:10px;
  }

  .gallery-v4-photo{
    position:relative;
    aspect-ratio:4/3;
    min-width:0;
    overflow:hidden;
    border:1px solid #e2e8f0;
    border-radius:18px;
    background:#f1f5f9;
    box-shadow:0 10px 24px rgba(15,23,42,.045);
  }

  .gallery-v4-photo img{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
  }

  .gallery-v4-photo::after{
    content:"";
    position:absolute;
    inset:auto 0 0;
    height:52%;
    background:linear-gradient(180deg,transparent,rgba(15,23,42,.46));
    pointer-events:none;
  }

  .gallery-v4-photo-top{
    position:absolute;
    z-index:2;
    top:8px;
    left:8px;
  }

  .gallery-v4-photo-top span{
    min-height:25px;
    display:inline-flex;
    align-items:center;
    padding:0 9px;
    border-radius:999px;
    background:rgba(255,255,255,.93);
    color:#00675f;
    font-size:9.5px;
    line-height:1;
    font-weight:950;
  }

  .gallery-v4-remove{
    position:absolute;
    z-index:3;
    right:8px;
    bottom:8px;
    min-height:30px;
    border:1px solid rgba(255,255,255,.58);
    border-radius:999px;
    padding:0 10px;
    background:rgba(255,255,255,.94);
    color:#b42318;
    font-size:10px;
    line-height:1;
    font-weight:950;
    cursor:pointer;
  }

  .gallery-v4-remove:disabled{opacity:.6;cursor:wait}

  .gallery-v4-empty{
    margin-top:11px;
    min-height:120px;
    display:flex;
    align-items:center;
    gap:14px;
    padding:18px;
    border:1px dashed #cbd5e1;
    border-radius:20px;
    background:#fff;
  }

  .gallery-v4-empty-icon{
    width:48px;
    height:48px;
    min-width:48px;
    display:grid;
    place-items:center;
    border-radius:16px;
    background:#eaf8f4;
    color:#00796f;
  }

  .gallery-v4-empty strong{
    display:block;
    color:#0f172a;
    font-size:14px;
    font-weight:950;
  }

  .gallery-v4-empty span{
    display:block;
    margin-top:4px;
    color:#64748b;
    font-size:11.5px;
    line-height:1.4;
    font-weight:720;
  }

  .gallery-v4-footnote{
    margin:12px 1px 0;
    color:#98a2b3;
    font-size:10px;
    line-height:1.4;
    font-weight:700;
  }

  @media(max-width:920px){
    .gallery-v4-grid{
      grid-template-columns:repeat(3,minmax(0,1fr));
    }
  }

  @media(max-width:760px){
    .gallery-v4-panel{
      border-radius:24px;
    }

    .gallery-v4-head{
      display:grid;
      grid-template-columns:1fr;
      gap:14px;
      padding:16px;
    }

    .gallery-v4-head h2{
      font-size:23px;
    }

    .gallery-v4-visibility{
      width:100%;
      min-width:0;
      box-sizing:border-box;
    }

    .gallery-v4-body{
      padding:16px;
    }

    .gallery-v4-upload{
      grid-template-columns:44px minmax(0,1fr);
      gap:12px;
      padding:14px;
    }

    .gallery-v4-upload-icon{
      width:44px;
      height:44px;
      border-radius:15px;
    }

    .gallery-v4-upload-actions{
      grid-column:1/-1;
      display:grid;
      grid-template-columns:1fr 1fr;
      width:100%;
    }

    .gallery-v4-btn{
      min-height:46px;
    }

    .gallery-v4-grid{
      grid-template-columns:repeat(2,minmax(0,1fr));
      gap:8px;
    }

    .gallery-v4-photo{
      border-radius:16px;
    }
  }
`;
